import React, { useState, useMemo, useEffect } from 'react';
import { useLMS } from '../../context/LMSContext';
import { itemTypeIcons, ZOOM_LOGO_URL } from '../../data/lmsData';
import AssignmentSubmission from '../student/AssignmentSubmission';
import RichTextEditor from './RichTextEditor';
import StudentReflections from '../student/StudentReflections';

const ModuleView = () => {
  const {
    currentUser,
    selectedCohortId,
    selectedWeek,
    getCohortById,
    getModulesForCohort,
    getVisibleModulesForCohort,
    setCurrentView,
    setSelectedWeek,
    addModuleItem,
    updateModuleItem,
    deleteModuleItem,
    // Progress tracking functions
    markItemComplete,
    trackItemView,
    isItemComplete,
    isModuleComplete,
    getModuleProgress,
    // Submissions
    getSubmissionForItem,
    // Cohort-specific drag-and-drop
    loadCohortItemOverrides,
    updateCohortItemDay,
    updateCohortItemDueDate,
    updateCohortItemOrder,
    cohortItemOverrides,
    // Settings
    defaultZoomUrl
  } = useLMS();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  // Track which items are expanded - empty by default (all collapsed)
  const [expandedItems, setExpandedItems] = useState({});
  // Assignment submission modal state
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [submissionItem, setSubmissionItem] = useState(null);
  
  // Cohort-specific drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);

  // Track which days are collapsed (for students & staff) - default all expanded
  const [collapsedDays, setCollapsedDays] = useState({});
  
  const [newItem, setNewItem] = useState({
    type: 'video',
    title: '',
    content: '',
    videoUrl: '',
    videoType: 'youtube',
    dueDay: '',
    points: 0,
    questionCount: 0,
    timeLimit: 0
  });

  // Keep a snapshot of the original item when the edit modal opens
  // so we can detect which fields actually changed
  const [originalEditItem, setOriginalEditItem] = useState(null);

  const isAdmin = currentUser?.role === 'admin';
  const isProfessor = currentUser?.role === 'professor';
  const isStaff = isAdmin || isProfessor;
  const isStudent = currentUser?.role === 'student';
  const cohort = getCohortById(selectedCohortId);
  const modules = getModulesForCohort(selectedCohortId);
  const currentModule = modules?.find(m => m.weekNumber === selectedWeek);

  // Load cohort item overrides when cohort changes
  useEffect(() => {
    if (selectedCohortId && loadCohortItemOverrides) {
      loadCohortItemOverrides(selectedCohortId);
    }
  }, [selectedCohortId, loadCohortItemOverrides]);

  // =========================================================================
  // DATE CALCULATION FOR EACH DAY OF THE WEEK
  // Calculate actual dates based on cohort start date and week number
  // =========================================================================
  const weekDates = useMemo(() => {
    const dates = {};
    const daysOrder = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    // Get cohort start date
    const startDateStr = cohort?.startDate || cohort?.start_date;
    if (!startDateStr || !selectedWeek) {
      return dates;
    }

    // Parse as local date to avoid timezone issues
    const [year, month, day] = startDateStr.split('-').map(Number);
    const cohortStart = new Date(year, month - 1, day);
    
    // Calculate the start of the selected week (Week 1 starts on cohort start date)
    // Each week is 7 days, so Week N starts at (N-1) * 7 days from start
    const weekStartOffset = (selectedWeek - 1) * 7;
    
    // Get the Sunday of the selected week
    // First, find out what day of week the cohort starts on
    const cohortStartDay = cohortStart.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Calculate the Sunday of Week 1 (may be before cohort start if cohort doesn't start on Sunday)
    const week1Sunday = new Date(cohortStart);
    week1Sunday.setDate(cohortStart.getDate() - cohortStartDay);
    
    // Now get the Sunday of the selected week
    const selectedWeekSunday = new Date(week1Sunday);
    selectedWeekSunday.setDate(week1Sunday.getDate() + weekStartOffset);

    // Generate dates for each day of the week
    // The rendering order is Mon-Sat then Sunday at the bottom.
    // We calculate Sunday through Saturday (index 0-6) from selectedWeekSunday,
    // but also provide a "next Sunday" date so that the Sunday row at the bottom
    // shows the chronologically correct date (the day AFTER Saturday, not before Monday).
    daysOrder.forEach((dayName, index) => {
      const dayDate = new Date(selectedWeekSunday);
      dayDate.setDate(selectedWeekSunday.getDate() + index);
      
      dates[dayName] = {
        date: dayDate,
        formatted: dayDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }),
        fullFormatted: dayDate.toLocaleDateString('en-US', { 
          weekday: 'long',
          month: 'long', 
          day: 'numeric',
          year: 'numeric'
        }),
        isToday: isSameDay(dayDate, new Date())
      };
    });

    // Since the week renders Monday first and Sunday last, compute the 
    // END-of-week Sunday (Saturday + 1) so the date shown is chronological.
    const endOfWeekSunday = new Date(selectedWeekSunday);
    endOfWeekSunday.setDate(selectedWeekSunday.getDate() + 7);
    dates['SundayEnd'] = {
      date: endOfWeekSunday,
      formatted: endOfWeekSunday.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      }),
      fullFormatted: endOfWeekSunday.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      }),
      isToday: isSameDay(endOfWeekSunday, new Date())
    };

    return dates;
  }, [cohort?.startDate, cohort?.start_date, selectedWeek]);

  // Get progress for current module (with safety checks)
  const moduleProgress = (getModuleProgress && selectedWeek) 
    ? getModuleProgress(selectedWeek) 
    : { completed: 0, total: 0, percentage: 0 };

  // Use the same visibility logic as the sidebar (getVisibleModulesForCohort)
  // so a module is considered hidden for a student only when it is not in the
  // visible set (respecting both the is_visible flag AND any visibleAfter
  // schedule). Checking isVisible alone would over-block schedule-unlocked
  // weeks and — more importantly — leave a gap on mobile where a student who
  // deep-links to a locked week could bypass the block if cohort overrides
  // weren't normalised identically.
  const visibleModuleIds = useMemo(() => {
    if (!selectedCohortId) return null;
    const list = getVisibleModulesForCohort(selectedCohortId) || [];
    return new Set(list.map(m => m.id));
  }, [selectedCohortId, getVisibleModulesForCohort]);
  const isModuleHidden = currentModule && visibleModuleIds
    ? !visibleModuleIds.has(currentModule.id)
    : false;

  // =========================================================================
  // COHORT-SPECIFIC DRAG AND DROP HANDLERS
  // =========================================================================
  const handleCohortDragStart = (e, item) => {
    if (!isStaff) return;
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
    setTimeout(() => {
      e.target.classList.add('dragging');
    }, 0);
  };

  const handleCohortDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedItem(null);
    setDragOverDay(null);
  };

  const handleCohortDragOver = (e, day) => {
    if (!isStaff) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(day);
  };

  const handleCohortDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverDay(null);
    }
  };

  const handleCohortDrop = async (e, targetDay) => {
    e.preventDefault();
    setDragOverDay(null);

    if (!draggedItem || !isStaff || !selectedCohortId) return;

    const currentDay = draggedItem.dueDay || draggedItem.due_day;
    
    // If dropping on the same day, no change needed
    if (currentDay === targetDay) {
      setDraggedItem(null);
      return;
    }

    // Update the item's day for THIS COHORT ONLY
    const result = await updateCohortItemDay(selectedCohortId, draggedItem.id, targetDay);

    if (result?.error) {
      alert(`Failed to move item: ${result.error}`);
    }

    setDraggedItem(null);
  };

  // Check if an item has a cohort override
  const hasOverride = (itemId) => {
    return cohortItemOverrides?.[selectedCohortId]?.[itemId] != null;
  };

  // =========================================================================
  // TOGGLE DAY COLLAPSED/EXPANDED (Fix #3: collapsible daily lists)
  // =========================================================================
  const toggleDayCollapsed = (day) => {
    setCollapsedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  // Expand/collapse all days at once
  const collapseAllDays = () => {
    const allCollapsed = {};
    ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].forEach(d => {
      allCollapsed[d] = true;
    });
    setCollapsedDays(allCollapsed);
  };

  const expandAllDays = () => {
    setCollapsedDays({});
  };

  // =========================================================================
  // WITHIN-DAY REORDER (Fix #2: move items up/down within the same day)
  // =========================================================================
 const handleReorderItem = async (item, direction, dayItems) => {
    if (!isStaff) return;
    
    const currentIndex = dayItems.findIndex(i => i.id === item.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Bounds check
    if (targetIndex < 0 || targetIndex >= dayItems.length) return;
    
    // Build a new ordering: assign sequential order values (1-based) to ALL
    // items in this day, with the moved item in its new position. This avoids
    // issues where multiple items share the same itemOrder default value.
    const reordered = [...dayItems];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    
    if (selectedCohortId && updateCohortItemOrder) {
      // Cohort view: update overrides for ALL items in this day so ordering
      // is fully explicit and deterministic.
      const promises = reordered.map((reorderedItem, idx) => 
        updateCohortItemOrder(selectedCohortId, reorderedItem.id, idx + 1)
      );
      await Promise.all(promises);
    } else {
      // Master view: update master items
      const promises = reordered.map((reorderedItem, idx) => 
        updateModuleItem(reorderedItem.id, { ...reorderedItem, itemOrder: idx + 1 })
      );
      await Promise.all(promises);
    }
  };

  if (!currentModule) {
    return (
      <div className="module-view">
        <div className="empty-state">
          <h2>Module not found</h2>
          <p>Please select a module from the sidebar</p>
        </div>
      </div>
    );
  }

  // Block students from viewing hidden modules
  if (isStudent && isModuleHidden) {
    return (
      <div className="module-view">
        <div className="empty-state">
          <span style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</span>
          <h2>Module Not Available</h2>
          <p>This module is not yet available. Please check back later or contact your instructor.</p>
          <button 
            className="btn-primary" 
            style={{ marginTop: '20px' }}
            onClick={() => setCurrentView('dashboard')}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Handle clicking on an item - toggle expand AND track view for students
  const handleItemClick = (item) => {
    const itemId = item.id;
    const isCurrentlyExpanded = expandedItems[itemId];
    
    // Toggle expanded state
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !isCurrentlyExpanded
    }));
    
    // If student is expanding (not collapsing), track that they viewed it
    // But do NOT auto-mark as complete - student must click checkmark for that
    if (isStudent && !isCurrentlyExpanded && trackItemView) {
      trackItemView(itemId);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();

    // addModuleItem() takes the module's id. selectedWeek is a week number, so
    // passing it straight through used to fail silently and drop the item.
    if (!currentModule?.id) {
      alert('Could not find this week. Refresh the page and try again.');
      return;
    }

    const result = await addModuleItem(currentModule.id, newItem);
    if (result?.error) {
      alert(`Failed to add item: ${result.error}`);
      return;
    }

    setShowAddModal(false);
    setNewItem({
      type: 'video',
      title: '',
      content: '',
      videoUrl: '',
      videoType: 'youtube',
      dueDay: '',
      points: 0,
      questionCount: 0,
      timeLimit: 0
    });
  };

  const handleEditItem = async (e) => {
    e.preventDefault();

    // Detect if dueDay was changed
    const originalDueDay = originalEditItem?.dueDay || originalEditItem?.due_day || '';
    const newDueDay = editingItem.dueDay || '';
    const dueDayChanged = newDueDay !== originalDueDay;

    // If a cohort is selected and the due day changed, apply it as a
    // cohort-specific override instead of modifying the master template.
    if (selectedCohortId && dueDayChanged && updateCohortItemDay) {
      const dayResult = await updateCohortItemDay(selectedCohortId, editingItem.id, newDueDay || null);
      if (dayResult?.error) {
        alert(`Failed to update due day: ${dayResult.error}`);
      }
    }

    // Build the payload for the master update, but EXCLUDE dueDay when a
    // cohort is selected so it doesn't overwrite the master template's day.
    const masterPayload = { ...editingItem };
    if (selectedCohortId) {
      delete masterPayload.dueDay;
      delete masterPayload.due_day;
    }

    updateModuleItem(editingItem.id, masterPayload);
    setShowEditModal(false);
    setEditingItem(null);
    setOriginalEditItem(null);
  };

  const handleDeleteItem = async (itemId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this item? This will remove it from ALL cohorts.')) {
      return;
    }

    // deleteModuleItem() takes the item id only - the extra week argument that
    // used to be passed here shifted the id out of position and deleted nothing.
    const result = await deleteModuleItem(itemId);
    if (result?.error) {
      alert(`Failed to delete item: ${result.error}`);
    }
  };

  const openEditModal = (item, e) => {
    e.stopPropagation();
    setOriginalEditItem({ ...item });
    setEditingItem({ ...item });
    setShowEditModal(true);
  };

  const getTypeLabel = (type) => {
    const labels = {
      'start-here': 'Start Here',
      'about-course': 'About Course',
      'video': 'Video',
      'live-lecture': 'Live Lecture',
      'assignment': 'Assignment',
      'quiz': 'Quiz',
      'exam': 'Exam',
      'workbook': 'Workbook',
      'resource': 'Resource',
      'reflection': 'Reflection'
    };
    return labels[type] || type;
  };

  // Helper to render item icon - handles Zoom logo for live-lecture
  const renderItemIcon = (type) => {
    if (type === 'live-lecture') {
      return (
        <img 
          src={ZOOM_LOGO_URL} 
          alt="Zoom" 
          className="zoom-icon"
          style={{ width: '24px', height: '24px', objectFit: 'contain' }}
        />
      );
    }
    return itemTypeIcons[type] || '📄';
  };

  // Helper to check if two dates are the same day
  function isSameDay(date1, date2) {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  // Checkmark component - clickable for students
  const CompletionCheckmark = ({ isComplete, itemId, onClick }) => (
    <div 
      className={`completion-checkmark-wrapper`}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onClick) onClick(itemId);
      }}
    >
      <div 
        className={`completion-checkmark ${isComplete ? 'complete' : 'incomplete'} clickable`}
        title={isComplete ? 'Click to mark incomplete' : 'Click to mark complete'}
      >
        {isComplete ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        ) : (
          <div className="empty-circle"></div>
        )}
      </div>
      <span className={`completion-label ${isComplete ? 'complete' : ''}`}>
        {isComplete ? 'Done' : 'Mark Done'}
      </span>
    </div>
  );

  const renderItemContent = (item) => {
    switch (item.type) {
      case 'video': {
        // Extract YouTube video ID for embedding
        const getYouTubeId = (url) => {
          if (!url) return null;
          const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
          return match ? match[1] : null;
        };
        const ytId = getYouTubeId(item.videoUrl);

        return (
          <div className="item-content video-content">
            {item.videoUrl ? (
              ytId ? (
                <div className="video-embed" style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '8px', marginBottom: '8px' }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${ytId}`}
                    title={item.title || 'Video'}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              ) : (
                <div className="video-placeholder">
                  <span className="play-icon">▶️</span>
                  <p>External Video</p>
                  <a 
                    href={item.videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Open Video
                  </a>
                </div>
              )
            ) : (
              <div className="video-placeholder empty">
                <span>🎬</span>
                <p>No video uploaded yet</p>
              </div>
            )}
            {item.description && <div className="rich-description" style={{ marginTop: '8px' }} dangerouslySetInnerHTML={{ __html: item.description }} />}
            {item.duration && <span className="duration-badge">⏱️ {item.duration}</span>}
          </div>
        );
      }
      
      case 'assignment':
      case 'quiz':
      case 'exam': {
        const itemDueDate = item.dueDate ? new Date(item.dueDate) : null;
        const isPastDue = itemDueDate ? new Date() > itemDueDate : false;
        const isApproachingDue = itemDueDate && !isPastDue ? (itemDueDate - new Date()) < 24 * 60 * 60 * 1000 : false;

        return (
          <div className="item-content assessment-content">
            {item.description && <div className="rich-description" dangerouslySetInnerHTML={{ __html: item.description }} />}
            <div className="assessment-meta">
              {item.points && <span className="meta-badge">📊 {item.points} points</span>}
              {item.questionCount && <span className="meta-badge">❓ {item.questionCount} questions</span>}
              {item.timeLimit && <span className="meta-badge">⏱️ {item.timeLimit} min</span>}
              {item.dueDay && <span className="meta-badge">📅 Due {item.dueDay}</span>}
              {itemDueDate && (
                <span className={`meta-badge ${isPastDue ? 'past-due' : isApproachingDue ? 'approaching-due' : 'has-due-date'}`}>
                  {isPastDue ? '🔒' : '📅'} Due {itemDueDate.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </div>
            {/* Due Date Picker for Staff (cohort view only) */}
            {isStaff && selectedCohortId && (
              <div className="due-date-picker" onClick={(e) => e.stopPropagation()}>
                <label>Due Date/Time:</label>
                <input
                  type="datetime-local"
                  value={item.dueDate ? new Date(new Date(item.dueDate).getTime() - new Date(item.dueDate).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const d = new Date(e.target.value);
                      // Default to 11:59 PM if time is midnight (user likely only picked a date)
                      if (d.getHours() === 0 && d.getMinutes() === 0) {
                        d.setHours(23, 59, 0, 0);
                      }
                      updateCohortItemDueDate(selectedCohortId, item.id, d.toISOString());
                    } else {
                      updateCohortItemDueDate(selectedCohortId, item.id, null);
                    }
                  }}
                />
                {item.dueDate && (
                  <button
                    className="btn-icon small"
                    title="Clear due date"
                    onClick={() => updateCohortItemDueDate(selectedCohortId, item.id, null)}
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
            {/* File Downloads if available */}
            {(() => {
              const files = item.attachments || (item.pdfUrl || item.pdf_url ? [{ url: item.pdfUrl || item.pdf_url, name: item.pdfName || item.pdf_name || 'Download PDF' }] : []);
              if (files.length === 0) return null;
              return (
                <div className="pdf-download-section">
                  {files.map((file, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: files.length > 1 ? '6px' : 0 }}>
                      <a 
                        href={file.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn-pdf-download"
                        onClick={(e) => e.stopPropagation()}
                      >
                        📥 Download
                      </a>
                      <span className="pdf-name">{file.name}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
            {/* Submit Assignment Button for Students */}
            {isStudent && (
              <div className="assignment-actions">
                {isPastDue ? (
                  <span className="past-due-notice">🔒 Submission closed - past due date</span>
                ) : (
                  <button 
                    className="btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSubmissionItem(item);
                      setShowSubmissionModal(true);
                    }}
                  >
                    📝 Submit Assignment
                  </button>
                )}
              </div>
            )}
          </div>
        );
      }
      
      case 'start-here':
      case 'about-course':
        return (
          <div className="item-content text-content">
            <p>{item.content}</p>
          </div>
        );
      
      case 'workbook': {
        const wbDueDate = item.dueDate ? new Date(item.dueDate) : null;
        const wbPastDue = wbDueDate ? new Date() > wbDueDate : false;

        return (
          <div className="item-content workbook-content">
            {item.description && <div className="rich-description" dangerouslySetInnerHTML={{ __html: item.description }} />}
            <div className="assessment-meta">
              {item.questionCount && <span className="meta-badge">📝 {item.questionCount || 0} questions</span>}
              {item.points && <span className="meta-badge">📊 {item.points} points</span>}
              {wbDueDate && (
                <span className={`meta-badge ${wbPastDue ? 'past-due' : 'has-due-date'}`}>
                  {wbPastDue ? '🔒' : '📅'} Due {wbDueDate.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </div>
            {/* Due Date Picker for Staff (cohort view only) */}
            {isStaff && selectedCohortId && (
              <div className="due-date-picker" onClick={(e) => e.stopPropagation()}>
                <label>Due Date/Time:</label>
                <input
                  type="datetime-local"
                  value={item.dueDate ? new Date(new Date(item.dueDate).getTime() - new Date(item.dueDate).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const d = new Date(e.target.value);
                      // Default to 11:59 PM if time is midnight (user likely only picked a date)
                      if (d.getHours() === 0 && d.getMinutes() === 0) {
                        d.setHours(23, 59, 0, 0);
                      }
                      updateCohortItemDueDate(selectedCohortId, item.id, d.toISOString());
                    } else {
                      updateCohortItemDueDate(selectedCohortId, item.id, null);
                    }
                  }}
                />
                {item.dueDate && (
                  <button
                    className="btn-icon small"
                    title="Clear due date"
                    onClick={() => updateCohortItemDueDate(selectedCohortId, item.id, null)}
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
            {/* File Downloads */}
            {(() => {
              const files = item.attachments || (item.pdfUrl || item.pdf_url ? [{ url: item.pdfUrl || item.pdf_url, name: item.pdfName || item.pdf_name || 'Download PDF' }] : []);
              if (files.length > 0) {
                return (
                  <div className="pdf-download-section">
                    {files.map((file, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: files.length > 1 ? '6px' : 0 }}>
                        <a 
                          href={file.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn-pdf-download"
                          onClick={(e) => e.stopPropagation()}
                        >
                          📥 Download
                        </a>
                        <span className="pdf-name">{file.name}</span>
                      </div>
                    ))}
                  </div>
                );
              }
              return <p className="no-pdf-notice">No files attached to this workbook.</p>;
            })()}
            {/* Submit completed workbook button for students */}
            {isStudent && (
              <div className="assignment-actions">
                {wbPastDue ? (
                  <span className="past-due-notice">🔒 Submission closed - past due date</span>
                ) : (
                  <button 
                    className="btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSubmissionItem(item);
                      setShowSubmissionModal(true);
                    }}
                  >
                    📤 Upload Completed Workbook
                  </button>
                )}
              </div>
            )}
          </div>
        );
      }
      
      case 'live-lecture':
        return (
          <div className="item-content live-lecture-content">
            {item.description && <div className="lecture-description rich-description" dangerouslySetInnerHTML={{ __html: item.description }} />}
            <div className="lecture-meta">
              {item.startTime && item.endTime && (
                <span className="meta-badge time-badge">🕐 {item.startTime} - {item.endTime}</span>
              )}
              {item.dueDay && <span className="meta-badge">📅 {item.dueDay}</span>}
            </div>
            <a 
              href={item.videoUrl || defaultZoomUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-join-zoom"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/11/Zoom_Logo_2022.svg/2560px-Zoom_Logo_2022.svg.png"
                alt="Zoom"
                className="zoom-btn-logo"
              />
              Join Lecture
            </a>
          </div>
        );
      
      case 'reflection':
        return (
          <div className="item-content reflection-content" onClick={(e) => e.stopPropagation()}>
            {isStudent ? (
              <StudentReflections
                moduleId={currentModule?.id}
                weekNumber={currentModule?.weekNumber || currentModule?.week_number}
                moduleTitle={currentModule?.title}
              />
            ) : isStaff ? (
              <div style={{ padding: '16px' }}>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 12px' }}>
                  Reflection questions are configured for this week. Student responses can be viewed and downloaded from the Reflections sidebar.
                </p>
                <button
                  className="btn-secondary"
                  onClick={() => setCurrentView('reflections')}
                  style={{ fontSize: '13px' }}
                >
                  Open Reflections Manager
                </button>
              </div>
            ) : null}
          </div>
        );

      default:
        return <div className="item-content">{item.content}</div>;
    }
  };

  return (
    <div className="module-view">
      {/* Header */}
      <div className="module-header">
        <div className="breadcrumb">
          <span onClick={() => setCurrentView('dashboard')}>Dashboard</span>
          <span className="separator">/</span>
          <span onClick={() => setCurrentView('cohort')}>{cohort?.name}</span>
          <span className="separator">/</span>
          <span className="current">Week {selectedWeek}</span>
        </div>
        
        <div className="header-row">
          <div className="header-title-section">
            <h1>{currentModule.title}</h1>
            {/* Module completion badge for students */}
            {isStudent && isModuleComplete && (
              <div className={`module-completion-badge ${isModuleComplete(selectedWeek) ? 'complete' : ''}`}>
                {isModuleComplete(selectedWeek) ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Complete!</span>
                  </>
                ) : (
                  <span>{moduleProgress.completed}/{moduleProgress.total} items</span>
                )}
              </div>
            )}
          </div>
          {isAdmin && (
            <button className="btn-primary" onClick={() => setShowAddModal(true)}>
              + Add Item
            </button>
          )}
        </div>

        {/* Progress bar for students */}
        {isStudent && moduleProgress.total > 0 && (
          <div className="mv-progress-container">
            <div className="mv-progress-top">
              <span className="mv-progress-label">Progress</span>
              <span className="mv-progress-count">{moduleProgress.completed} of {moduleProgress.total} items complete</span>
              <span className="mv-progress-pct">{moduleProgress.percentage}%</span>
            </div>
            <div className="mv-progress-track">
              <div 
                className="mv-progress-fill" 
                style={{ width: `${moduleProgress.percentage}%` }}
              ></div>
            </div>
          </div>
        )}

        {isAdmin && !selectedCohortId && (
          <div className="master-template-notice">
            <span>🔗</span> Master Template - Changes apply to ALL cohorts
            <div className="due-date-reminder">📅 Due dates are set per cohort. Select a cohort to configure assignment due dates.</div>
          </div>
        )}
        
        {isStaff && selectedCohortId && (
          <div className="cohort-specific-notice">
            <span>📌</span> Cohort View - Drag items to reschedule for this cohort only
          </div>
        )}
        
        {isStaff && isModuleHidden && (
          <div className="visibility-warning">
            <span>🔒</span> This module is currently hidden from students in this cohort
            {currentModule.visibleDate && (
              <span> (Scheduled for {new Date(currentModule.visibleDate).toLocaleDateString()})</span>
            )}
          </div>
        )}
      </div>

      {/* Module Navigation */}
      <div className="module-nav">
        <button 
          className="nav-btn"
          disabled={selectedWeek <= 1}
          onClick={() => setSelectedWeek(selectedWeek - 1)}
        >
          ← Previous
        </button>
        <span className="week-indicator">Week {selectedWeek} of 6</span>
        <button 
          className="nav-btn"
          disabled={selectedWeek >= 6}
          onClick={() => setSelectedWeek(selectedWeek + 1)}
        >
          Next →
        </button>
      </div>

      {/* Instruction for students */}
      {isStudent && (
        <div className="module-instructions">
          <p>👆 Click on each item below to expand and view the content. Click a day header to collapse/expand that day.</p>
        </div>
      )}

      {/* Expand All / Collapse All Day Controls */}
      <div className="day-collapse-controls">
        <button className="btn-collapse-all" onClick={expandAllDays} title="Expand all days">
          ▼ Expand All Days
        </button>
        <button className="btn-collapse-all" onClick={collapseAllDays} title="Collapse all days">
          ▶ Collapse All Days
        </button>
      </div>

      {/* Module Items - Grouped by Day */}
      <div className="module-items">
        {(() => {
          // Group items by day
          const daysOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
          const itemsByDay = {};
          const unscheduledItems = [];
          
          // Initialize all days for admin view
          if (isStaff) {
            daysOrder.forEach(day => {
              itemsByDay[day] = [];
            });
          }
          
          currentModule.items.forEach(item => {
            const day = item.dueDay || item.due_day;
            if (day && daysOrder.includes(day)) {
              if (!itemsByDay[day]) itemsByDay[day] = [];
              itemsByDay[day].push(item);
            } else {
              unscheduledItems.push(item);
            }
          });

          // Sort items within each day by item_order if available
          Object.keys(itemsByDay).forEach(day => {
           itemsByDay[day].sort((a, b) => (a.itemOrder || a.item_order || 0) - (b.itemOrder || b.item_order || 0));
          });

          const hasAnyItems = currentModule.items.length > 0;
          const hasDayGroupedItems = Object.keys(itemsByDay).some(day => itemsByDay[day].length > 0);

          // For students with no items, show empty state
          if (!hasAnyItems && isStudent) {
            return (
              <div className="empty-state">
                <span className="empty-icon">📭</span>
                <p>No items in this module yet</p>
              </div>
            );
          }

          return (
            <>
              {/* Render days in order */}
              {daysOrder.map(day => {
                const dayItems = itemsByDay[day] || [];
                
                // For students, skip empty days
                if (!isStaff && dayItems.length === 0) return null;

                const isDropTarget = dragOverDay === day;

                return (
                  <div 
                    key={day} 
                    className={`day-section ${dayItems.length === 0 ? 'empty-day' : ''} ${(day === 'Sunday' ? weekDates['SundayEnd'] : weekDates[day])?.isToday ? 'is-today' : ''} ${isDropTarget ? 'drop-target' : ''} ${collapsedDays[day] ? 'day-collapsed' : ''}`}
                    onDragOver={isStaff ? (e) => handleCohortDragOver(e, day) : undefined}
                    onDragLeave={isStaff ? handleCohortDragLeave : undefined}
                    onDrop={isStaff ? (e) => handleCohortDrop(e, day) : undefined}
                  >
                    <div 
                      className="day-header day-header-clickable"
                      onClick={() => toggleDayCollapsed(day)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className={`day-collapse-icon ${collapsedDays[day] ? '' : 'expanded'}`}>▶</span>
                      <div className="day-title-group">
                        <h3 className="day-title">{day}</h3>
                        {/* Use SundayEnd date for the Sunday row so it shows the chronologically 
                            correct date (the day after Saturday, not the Sunday before Monday) */}
                        {(() => {
                          const dateInfo = day === 'Sunday' ? weekDates['SundayEnd'] : weekDates[day];
                          return dateInfo ? (
                            <span className={`day-date ${dateInfo.isToday ? 'today' : ''}`}>
                              {dateInfo.formatted}
                              {dateInfo.isToday && <span className="today-badge">Today</span>}
                            </span>
                          ) : null;
                        })()}
                      </div>
                      {dayItems.length > 0 ? (
                        <span className="day-item-count">{dayItems.length} item{dayItems.length !== 1 ? 's' : ''}</span>
                      ) : (
                        <span className="day-item-count empty">No items</span>
                      )}
                      {isAdmin && (
                        <button 
                          className="btn-add-to-day"
                          onClick={(e) => {
                            e.stopPropagation();
                            setNewItem(prev => ({ ...prev, dueDay: day }));
                            setShowAddModal(true);
                          }}
                          title={`Add item to ${day}`}
                        >
                          +
                        </button>
                      )}
                    </div>
                    {!collapsedDays[day] && (dayItems.length > 0 || isDropTarget) && (
                      <div className={`day-items ${isDropTarget && dayItems.length === 0 ? 'drop-placeholder' : ''}`}>
                        {dayItems.map((item, itemIndex) => {
                          const itemComplete = isItemComplete ? isItemComplete(item.id) : false;
                          const isExpanded = expandedItems[item.id] || false;
                          const itemHasOverride = hasOverride(item.id);
                          const submission = isStudent && getSubmissionForItem ? getSubmissionForItem(item.id) : null;
                          const hasFeedback = submission?.feedback && submission.feedback.trim() !== '';

                          return (
                            <div 
                              key={item.id} 
                              className={`module-item ${item.type} ${itemComplete ? 'completed' : ''} ${isExpanded ? 'expanded' : 'collapsed'} ${isStaff ? 'draggable' : ''} ${itemHasOverride ? 'has-override' : ''}`}
                              onClick={() => handleItemClick(item)}
                              draggable={isStaff}
                              onDragStart={isStaff ? (e) => handleCohortDragStart(e, item) : undefined}
                              onDragEnd={isStaff ? handleCohortDragEnd : undefined}
                            >
                              <div className="item-header">
                                {isStaff && (
                                  <span className="drag-handle" title="Drag to move (this cohort only)">⋮⋮</span>
                                )}
                                {/* Fix #2: Within-day reorder arrows (staff only) */}
                                {isStaff && dayItems.length > 1 && (
                                  <div className="reorder-arrows" onClick={(e) => e.stopPropagation()}>
                                    <button
                                      className="btn-reorder"
                                      disabled={itemIndex === 0}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleReorderItem(item, 'up', dayItems);
                                      }}
                                      title="Move up"
                                    >
                                      ▲
                                    </button>
                                    <button
                                      className="btn-reorder"
                                      disabled={itemIndex === dayItems.length - 1}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleReorderItem(item, 'down', dayItems);
                                      }}
                                      title="Move down"
                                    >
                                      ▼
                                    </button>
                                  </div>
                                )}
                                {isStudent && (
                                  <CompletionCheckmark 
                                    isComplete={itemComplete} 
                                    itemId={item.id}
                                    onClick={(id) => {
                                      if (markItemComplete) {
                                        markItemComplete(id, !itemComplete);
                                      }
                                    }}
                                  />
                                )}
                                <div className="item-icon">{renderItemIcon(item.type)}</div>
                                <div className="item-info">
                                  <span className="item-type">
                                    {getTypeLabel(item.type)}
                                    {itemHasOverride && <span className="override-badge" title="Scheduled differently for this cohort">📌</span>}
                                    {hasFeedback && <span className="feedback-badge" title="Instructor feedback available">💬</span>}
                                  </span>
                                  <h3>{item.title}</h3>
                                </div>
                                {isAdmin && (
                                  <div className="item-actions">
                                    <button className="btn-icon" title="Edit" onClick={(e) => openEditModal(item, e)}>
                                      ✏️
                                    </button>
                                    <button className="btn-icon" title="Delete" onClick={(e) => handleDeleteItem(item.id, e)}>
                                      🗑️
                                    </button>
                                  </div>
                                )}
                                <div className="expand-indicator">
                                  {isExpanded ? '▼' : '▶'}
                                </div>
                              </div>
                              {isExpanded && (
                                <div className="item-content-wrapper">
                                  {renderItemContent(item)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Unscheduled items (no day assigned) */}
              {unscheduledItems.length > 0 && (
                <div className="day-section unscheduled">
                  <div className="day-header">
                    <span className="day-icon">📋</span>
                    <h3 className="day-title">Unscheduled</h3>
                    <span className="day-item-count">{unscheduledItems.length} item{unscheduledItems.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="day-items">
                    {unscheduledItems.map((item) => {
                      const itemComplete = isItemComplete ? isItemComplete(item.id) : false;
                      const isExpanded = expandedItems[item.id] || false;
                      const submission = isStudent && getSubmissionForItem ? getSubmissionForItem(item.id) : null;
                      const hasFeedback = submission?.feedback && submission.feedback.trim() !== '';

                      return (
                        <div 
                          key={item.id} 
                          className={`module-item ${item.type} ${itemComplete ? 'completed' : ''} ${isExpanded ? 'expanded' : 'collapsed'}`}
                          onClick={() => handleItemClick(item)}
                        >
                          <div className="item-header">
                            {isStudent && (
                              <CompletionCheckmark 
                                isComplete={itemComplete} 
                                itemId={item.id}
                                onClick={(id) => {
                                  if (markItemComplete) {
                                    markItemComplete(id, !itemComplete);
                                  }
                                }}
                              />
                            )}
                            
                            <div className="item-icon">{renderItemIcon(item.type)}</div>
                            <div className="item-info">
                              <span className="item-type">
                                {getTypeLabel(item.type)}
                                {hasFeedback && <span className="feedback-badge" title="Instructor feedback available">💬</span>}
                              </span>
                              <h3>{item.title}</h3>
                            </div>
                            
                            {isAdmin && (
                              <div className="item-actions">
                                <button className="btn-icon" title="Edit" onClick={(e) => openEditModal(item, e)}>
                                  ✏️
                                </button>
                                <button className="btn-icon" title="Delete" onClick={(e) => handleDeleteItem(item.id, e)}>
                                  🗑️
                                </button>
                              </div>
                            )}
                            
                            <div className="expand-indicator">
                              {isExpanded ? '▼' : '▶'}
                            </div>
                          </div>
                          
                          {isExpanded && (
                            <div className="item-content-wrapper">
                              {renderItemContent(item)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>


      {/* Add Item Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Module Item</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleAddItem}>
              <div className="form-row">
                <div className="form-group">
                  <label>Item Type</label>
                  <select 
                    value={newItem.type}
                    onChange={(e) => setNewItem({...newItem, type: e.target.value})}
                  >
                    <option value="start-here">🚀 Start Here</option>
                    <option value="about-course">ℹ️ About Course</option>
                    <option value="video">🎬 Video</option>
                    <option value="assignment">📝 Assignment</option>
                    <option value="quiz">❓ Quiz</option>
                    <option value="exam">📋 Exam</option>
                    <option value="workbook">📚 Workbook</option>
                    <option value="resource">🔗 Resource</option>
                    <option value="reflection">💭 Reflection</option>
                  </select>
                  {newItem.type === 'reflection' && (
                    <small style={{ display: 'block', marginTop: '4px', color: '#6b7280' }}>
                      Write the questions for this week under Reflections in the sidebar.
                    </small>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Title</label>
                  <input 
                    type="text"
                    placeholder="Enter item title"
                    value={newItem.title}
                    onChange={(e) => setNewItem({...newItem, title: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              {/* Text content fields */}
              {(newItem.type === 'start-here' || newItem.type === 'about-course') && (
                <div className="form-group">
                  <label>Content</label>
                  <textarea 
                    rows={4}
                    placeholder="Enter content text"
                    value={newItem.content}
                    onChange={(e) => setNewItem({...newItem, content: e.target.value})}
                  />
                </div>
              )}
              
              {/* Video fields */}
              {newItem.type === 'video' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Duration</label>
                      <input 
                        type="text"
                        placeholder="e.g., 45 min"
                        value={newItem.duration || ''}
                        onChange={(e) => setNewItem({...newItem, duration: e.target.value})}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Video Source</label>
                      <select 
                        value={newItem.videoType}
                        onChange={(e) => setNewItem({...newItem, videoType: e.target.value})}
                      >
                        <option value="youtube">📺 YouTube</option>
                        <option value="url">🔗 External URL</option>
                        <option value="upload">📤 Upload Video</option>
                      </select>
                    </div>
                  </div>
                  
                  {newItem.videoType !== 'upload' && (
                    <div className="form-group">
                      <label>Video URL</label>
                      <input 
                        type="url"
                        placeholder="https://youtube.com/watch?v=..."
                        value={newItem.videoUrl}
                        onChange={(e) => setNewItem({...newItem, videoUrl: e.target.value})}
                      />
                    </div>
                  )}
                  
                  {newItem.videoType === 'upload' && (
                    <div className="form-group">
                      <label>Upload Video</label>
                      <div className="file-upload">
                        <span>📹</span>
                        <p>Drag and drop or click to upload</p>
                        <input type="file" accept="video/*" />
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Assessment fields */}
              {(newItem.type === 'assignment' || newItem.type === 'quiz' || newItem.type === 'exam') && (
                <>
                  <div className="form-group">
                    <label>Description</label>
                    <RichTextEditor
                      value={newItem.description || ''}
                      onChange={(html) => setNewItem({...newItem, description: html})}
                      placeholder="Description / instructions..."
                      rows={3}
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Due Day</label>
                      <select 
                        value={newItem.dueDay}
                        onChange={(e) => setNewItem({...newItem, dueDay: e.target.value})}
                      >
                        <option value="">Select day...</option>
                        <option value="Monday">Monday</option>
                        <option value="Tuesday">Tuesday</option>
                        <option value="Wednesday">Wednesday</option>
                        <option value="Thursday">Thursday</option>
                        <option value="Friday">Friday</option>
                        <option value="Saturday">Saturday</option>
                        <option value="Sunday">Sunday</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>Points</label>
                      <input 
                        type="number"
                        min="0"
                        value={newItem.points}
                        onChange={(e) => setNewItem({...newItem, points: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                  
                  {(newItem.type === 'quiz' || newItem.type === 'exam') && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Number of Questions</label>
                        <input 
                          type="number"
                          min="1"
                          value={newItem.questionCount}
                          onChange={(e) => setNewItem({...newItem, questionCount: parseInt(e.target.value) || 0})}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Time Limit (minutes)</label>
                        <input 
                          type="number"
                          min="0"
                          value={newItem.timeLimit}
                          onChange={(e) => setNewItem({...newItem, timeLimit: parseInt(e.target.value) || 0})}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Workbook fields */}
              {newItem.type === 'workbook' && (
                <div className="form-group">
                  <label>Number of Questions</label>
                  <input 
                    type="number"
                    min="1"
                    value={newItem.questionCount}
                    onChange={(e) => setNewItem({...newItem, questionCount: parseInt(e.target.value) || 0})}
                  />
                </div>
              )}
              
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {showEditModal && editingItem && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Item</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleEditItem}>
              <div className="form-group">
                <label>Title</label>
                <input 
                  type="text"
                  value={editingItem.title}
                  onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
                  required
                />
              </div>
              
              {/* Content field for text-based items */}
              {['start-here', 'about-course', 'resource'].includes(editingItem.type) && (
                <div className="form-group">
                  <label>Content</label>
                  <textarea 
                    rows={4}
                    value={editingItem.content || ''}
                    onChange={(e) => setEditingItem({...editingItem, content: e.target.value})}
                  />
                </div>
              )}
              
              {/* Video fields */}
              {editingItem.type === 'video' && (
                <>
                  <div className="form-group">
                    <label>Description</label>
                    <RichTextEditor
                      value={editingItem.description || ''}
                      onChange={(html) => setEditingItem({...editingItem, description: html})}
                      placeholder="Description or notes..."
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>Video URL</label>
                    <input 
                      type="url"
                      placeholder="https://youtube.com/watch?v=..."
                      value={editingItem.videoUrl || ''}
                      onChange={(e) => setEditingItem({...editingItem, videoUrl: e.target.value})}
                    />
                  </div>
                </>
              )}

              {/* Live Lecture fields */}
              {editingItem.type === 'live-lecture' && (
                <>
                  <div className="form-group">
                    <label>Description</label>
                    <RichTextEditor
                      value={editingItem.description || ''}
                      onChange={(html) => setEditingItem({...editingItem, description: html})}
                      placeholder="Lecture topic or notes..."
                      rows={2}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Time</label>
                      <input
                        type="time"
                        value={editingItem.startTime || ''}
                        onChange={(e) => setEditingItem({...editingItem, startTime: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>End Time</label>
                      <input
                        type="time"
                        value={editingItem.endTime || ''}
                        onChange={(e) => setEditingItem({...editingItem, endTime: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Meeting URL</label>
                    <input
                      type="url"
                      placeholder={`Leave empty to use default: ${defaultZoomUrl}`}
                      value={editingItem.videoUrl || ''}
                      onChange={(e) => setEditingItem({...editingItem, videoUrl: e.target.value})}
                    />
                    <span className="form-hint">
                      {editingItem.videoUrl ? '✓ Using custom URL' : '📎 Will use default Zoom URL'}
                    </span>
                  </div>
                </>
              )}

              {/* Workbook fields */}
              {editingItem.type === 'workbook' && (
                <>
                  <div className="form-group">
                    <label>Description / Instructions</label>
                    <RichTextEditor
                      value={editingItem.description || ''}
                      onChange={(html) => setEditingItem({...editingItem, description: html})}
                      placeholder="Instructions for this workbook..."
                      rows={3}
                    />
                  </div>
                  {(() => {
                    const files = editingItem.attachments || (editingItem.pdfUrl ? [{ url: editingItem.pdfUrl, name: editingItem.pdfName || 'Attached PDF' }] : []);
                    if (files.length === 0) return null;
                    return (
                      <div className="form-group">
                        <label>Attached Documents</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {files.map((file, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'var(--gray-50)', borderRadius: '6px' }}>
                              <span>📄 {file.name}</span>
                              <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: 'var(--blue-primary)' }}>View</a>
                            </div>
                          ))}
                        </div>
                        <span className="form-hint">To add/remove files, edit this item from Master Modules</span>
                      </div>
                    );
                  })()}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Points (optional)</label>
                      <input
                        type="number"
                        min="0"
                        value={editingItem.points || 0}
                        onChange={(e) => setEditingItem({...editingItem, points: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Number of Questions</label>
                      <input
                        type="number"
                        min="0"
                        value={editingItem.questionCount || 0}
                        onChange={(e) => setEditingItem({...editingItem, questionCount: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                </>
              )}
              
              {/* Assessment fields */}
              {['assignment', 'quiz', 'exam'].includes(editingItem.type) && (
                <>
                  <div className="form-group">
                    <label>Description / Instructions</label>
                    <RichTextEditor
                      value={editingItem.description || ''}
                      onChange={(html) => setEditingItem({...editingItem, description: html})}
                      placeholder="Description / instructions..."
                      rows={3}
                    />
                  </div>
                  {(() => {
                    const files = editingItem.attachments || (editingItem.pdfUrl ? [{ url: editingItem.pdfUrl, name: editingItem.pdfName || 'Attached PDF' }] : []);
                    if (files.length === 0) return null;
                    return (
                      <div className="form-group">
                        <label>Attached Documents</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {files.map((file, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'var(--gray-50)', borderRadius: '6px' }}>
                              <span>📄 {file.name}</span>
                              <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: 'var(--blue-primary)' }}>View</a>
                            </div>
                          ))}
                        </div>
                        <span className="form-hint">To add/remove files, edit this item from Master Modules</span>
                      </div>
                    );
                  })()}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Points</label>
                      <input 
                        type="number"
                        value={editingItem.points || 0}
                        onChange={(e) => setEditingItem({...editingItem, points: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Due Day{selectedCohortId && <span style={{ fontWeight: 'normal', fontSize: '11px', color: 'var(--gray-500, #6b7280)' }}> (this cohort only)</span>}</label>
                      <select 
                        value={editingItem.dueDay || ''}
                        onChange={(e) => setEditingItem({...editingItem, dueDay: e.target.value})}
                      >
                        <option value="">Select day...</option>
                        <option value="Monday">Monday</option>
                        <option value="Tuesday">Tuesday</option>
                        <option value="Wednesday">Wednesday</option>
                        <option value="Thursday">Thursday</option>
                        <option value="Friday">Friday</option>
                        <option value="Saturday">Saturday</option>
                        <option value="Sunday">Sunday</option>
                      </select>
                    </div>
                  </div>
                  {['quiz', 'exam'].includes(editingItem.type) && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Number of Questions</label>
                        <input
                          type="number"
                          min="1"
                          value={editingItem.questionCount || 0}
                          onChange={(e) => setEditingItem({...editingItem, questionCount: parseInt(e.target.value) || 0})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Time Limit (minutes)</label>
                        <input
                          type="number"
                          min="0"
                          value={editingItem.timeLimit || 0}
                          onChange={(e) => setEditingItem({...editingItem, timeLimit: parseInt(e.target.value) || 0})}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Lecture / Discussion / Announcement fields */}
              {['lecture', 'discussion', 'announcement'].includes(editingItem.type) && (
                <div className="form-group">
                  <label>Description</label>
                  <RichTextEditor
                    value={editingItem.description || ''}
                    onChange={(html) => setEditingItem({...editingItem, description: html})}
                    placeholder="Description..."
                    rows={3}
                  />
                </div>
              )}

              {/* PDF type fields */}
              {editingItem.type === 'pdf' && (
                <>
                  <div className="form-group">
                    <label>Description</label>
                    <RichTextEditor
                      value={editingItem.description || ''}
                      onChange={(html) => setEditingItem({...editingItem, description: html})}
                      placeholder="Description..."
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>PDF URL</label>
                    <input
                      type="url"
                      value={editingItem.videoUrl || ''}
                      onChange={(e) => setEditingItem({...editingItem, videoUrl: e.target.value})}
                    />
                  </div>
                </>
              )}
              
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assignment Submission Modal for Students */}
      {isStudent && (
        <AssignmentSubmission
          item={submissionItem}
          isOpen={showSubmissionModal}
          onClose={() => {
            setShowSubmissionModal(false);
            setSubmissionItem(null);
          }}
          onSubmitted={() => {
            // Optionally refresh or show success message
          }}
        />
      )}
    </div>
  );
};

export default ModuleView;
